import React from "react";
import { defaultPfpUrl } from "../../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { cn } from "../../../lib/utils";
import { parseShareMetadata } from "../../feed_page/shareUtils";
import { getSharedMessagePreview } from "../chatListUtils";
import { getMessageImageUrls } from "../../../lib/contentImages";

/**
 * Preview source for a message being replied to.
 * @param {object|null|undefined} message - Full parent message, or a reply with parent_* fields
 * @param {{ fromParentFields?: boolean }} [options]
 * @returns {{ content: string, deleted: boolean, messageType: string, metadata: string|object }}
 */
export function getReplyPreviewSource(message, options = {}) {
  if (!message) {
    return { content: "", deleted: false, messageType: "text", metadata: "" };
  }
  if (options.fromParentFields) {
    return {
      content: message.parent_content || "",
      deleted: Boolean(message.parent_deleted_at),
      messageType: message.parent_message_type || "text",
      metadata: message.parent_metadata || "",
    };
  }
  return {
    content: getSharedMessagePreview(message),
    deleted: Boolean(message.deleted_at),
    messageType: message.message_type || "text",
    metadata: message.metadata || "",
  };
}

/**
 * Compact pfp + name + caption for a shared post or comment.
 * @param {Object} props
 * @param {object} props.meta
 * @returns {JSX.Element}
 */
function ShareReplySnippet({ meta }) {
  const comment = meta?.comment;
  const name = comment
    ? comment.author_name || meta.author_name || "Author"
    : meta.author_name || "Author";
  const pfp = comment
    ? comment.author_pfp || meta.author_pfp || ""
    : meta.author_pfp || "";
  const caption = comment
    ? comment.content || ""
    : meta.caption ||
      (meta.content_type === "event" ? "Event" : "Post");

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarImage
          src={pfp || defaultPfpUrl}
          alt=""
          onError={(event) => {
            event.target.src = defaultPfpUrl;
          }}
        />
        <AvatarFallback className="text-[10px]">
          {name.slice(0, 1).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-semibold text-text-primary">
          {name}
        </div>
        {caption ? (
          <div className="truncate text-xs text-text-muted">{caption}</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Faded quote of the message being replied to.
 * @param {Object} props
 * @param {object} props.source
 * @param {boolean} [props.isOwnMessage]
 * @param {() => void} [props.onClick]
 * @returns {JSX.Element | null}
 */
function ReplyPreview({ source, isOwnMessage = false, onClick }) {
  if (!source) return null;

  const isShare =
    source.messageType === "post" || source.messageType === "event";
  const meta = isShare
    ? parseShareMetadata({
        metadata: source.metadata,
        message_type: source.messageType,
      })
    : null;
  const hasShareSnippet = Boolean(meta) && !source.deleted;
  const imageUrls =
    !source.deleted && source.messageType === "image"
      ? getMessageImageUrls({
          metadata: source.metadata,
          message_type: source.messageType,
        })
      : [];
  const previewText = source.deleted
    ? "This message was deleted"
    : source.content || "";
  if (!hasShareSnippet && !previewText && !imageUrls.length) return null;

  const body = source.deleted ? (
    <span className="italic">{previewText}</span>
  ) : hasShareSnippet ? (
    <ShareReplySnippet meta={meta} />
  ) : (
    <span className="flex min-w-0 items-center gap-2">
      {imageUrls[0] ? (
        <img
          src={imageUrls[0]}
          alt=""
          className="h-8 w-8 shrink-0 rounded-sm object-cover"
        />
      ) : null}
      <span className="line-clamp-2">
        {previewText || (imageUrls.length > 1 ? `${imageUrls.length} photos` : "Photo")}
      </span>
    </span>
  );

  const className = cn(
    "mb-1 flex max-w-xs items-stretch gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring lg:max-w-md",
    isOwnMessage && "flex-row-reverse"
  );
  const bubble = (
    <span
      className={cn(
        "min-w-0 max-w-full rounded-2xl bg-message-received px-3 py-1.5 text-left text-xs",
        hasShareSnippet ? "py-1" : "text-text-muted opacity-60"
      )}
    >
      {body}
    </span>
  );
  const rail = (
    <span
      className="w-0.5 shrink-0 self-stretch rounded-full bg-ui-border"
      aria-hidden
    />
  );

  if (typeof onClick === "function") {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        aria-label="Jump to original message"
      >
        {rail}
        {bubble}
      </button>
    );
  }

  return (
    <div className={className}>
      {rail}
      {bubble}
    </div>
  );
}

export default ReplyPreview;
